/**
 * Business date-range picker for the reporting tables.
 *
 * Deliberately NOT a generalisation of `ghl-dashboard/TimeRangeFilter`. That one
 * carries a `{ from: Date; until: Date; label: string }` value with ops-scale
 * presets (5m/15m/1h) and no timezone concept; this one carries a half-open ISO
 * UTC `DateRangeFilter` resolved in the ACCOUNT's timezone. Sharing one component
 * would mean a generic value type plus conversion adapters at both call sites —
 * more coupling than the popover shell is worth.
 */

import { useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CalendarDays, ChevronDown } from 'lucide-react'
import {
  DATE_PRESETS,
  resolveDatePreset,
  type DatePresetKey,
  type DateRangeFilter,
} from '@limo/shared'

interface Props {
  readonly label: string
  readonly value: DateRangeFilter | undefined
  readonly timezone: string
  readonly onChange: (next: DateRangeFilter | undefined) => void
}

/** `2026-01-04` for a date input, read in UTC (values are stored as UTC). */
function toDateInputValue(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

function summarise(value: DateRangeFilter | undefined): string | null {
  if (!value?.from && !value?.to) return null

  const fmt = (iso: string): string =>
    new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    })

  if (value.from && value.to) {
    // `to` is EXCLUSIVE, so subtract a day when describing the range to a human:
    // an August range stored as [Aug 1, Sep 1) reads as "Aug 1 – Aug 31".
    const lastDay = new Date(new Date(value.to).getTime() - 86_400_000).toISOString()
    const start = fmt(value.from)
    const end = fmt(lastDay)
    return start === end ? start : `${start} – ${end}`
  }
  if (value.from) return `From ${fmt(value.from)}`
  return `Before ${fmt(value.to!)}`
}

export function ReportDateRangeFilter({ label, value, timezone, onChange }: Props) {
  const [open, setOpen] = useState(false)

  const applyPreset = (key: DatePresetKey): void => {
    const resolved = resolveDatePreset(key, timezone, new Date())
    // 'all_time' resolves to {} — treat that as clearing the filter so no
    // unbounded predicate is sent.
    onChange(resolved.from || resolved.to ? resolved : undefined)
    setOpen(false)
  }

  /**
   * Custom bounds come from `<input type="date">`, which yields a bare
   * `YYYY-MM-DD`. Interpret those as midnight in the ACCOUNT's timezone (not the
   * browser's) so a hand-picked range agrees with the presets, and make the end
   * date inclusive for the user by advancing it one day into the exclusive bound.
   */
  const setBound = (which: 'from' | 'to', dateStr: string): void => {
    if (dateStr === '') {
      const next = { ...value, [which]: undefined }
      onChange(next.from || next.to ? next : undefined)
      return
    }

    const [y, m, d] = dateStr.split('-').map(Number)
    const dayStart = resolveDatePreset('today', timezone, new Date(Date.UTC(y, m - 1, d, 12)))

    onChange({
      ...value,
      [which]: which === 'from' ? dayStart.from : dayStart.to,
    })
  }

  const summary = summarise(value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <CalendarDays className="h-3.5 w-3.5" />
          <span className="max-w-[190px] truncate">
            {label}
            {summary ? `: ${summary}` : ''}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <p className="text-xs font-medium text-muted-foreground mb-2">{label}</p>

        <div className="grid grid-cols-3 gap-1.5 mb-4">
          {DATE_PRESETS.map((preset) => (
            <Button
              key={preset.key}
              variant="outline"
              size="sm"
              className="text-xs h-8"
              onClick={() => applyPreset(preset.key)}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        <div className="space-y-3 border-t pt-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground" htmlFor={`${label}-from`}>
              From
            </label>
            <Input
              id={`${label}-from`}
              type="date"
              value={toDateInputValue(value?.from)}
              onChange={(e) => setBound('from', e.target.value)}
              className="mt-1 text-sm h-9"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground" htmlFor={`${label}-to`}>
              To <span className="font-normal">(inclusive)</span>
            </label>
            <Input
              id={`${label}-to`}
              type="date"
              // Show the inclusive last day even though the stored bound is
              // exclusive, so the field matches what the operator typed.
              value={toDateInputValue(
                value?.to
                  ? new Date(new Date(value.to).getTime() - 86_400_000).toISOString()
                  : undefined
              )}
              onChange={(e) => setBound('to', e.target.value)}
              className="mt-1 text-sm h-9"
            />
          </div>

          <p className="text-[11px] text-muted-foreground">
            Dates use your business timezone ({timezone}).
          </p>

          {summary && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                onChange(undefined)
                setOpen(false)
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
